import React, { useState, useCallback } from 'react';
import { Box, Typography, Avatar, Button, Chip } from '@mui/material';
import { getSwipeQueueMeta } from 'constants/swipeQueues';
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from 'framer-motion';
import BookInfoContent from 'components/BookInfoContent';
import SwipeOverlay from './SwipeOverlay';
import { bookCoverImgSx } from 'utils/bookCoverDisplay';
import { setOpenLibraryCoverSize, OL_COVER_SIZE } from 'services/openLibraryService';

const DEAD_ZONE = 32;
const SWIPE_COMMIT = 100;

const cardShellSx = {
  position: 'relative',
  borderRadius: 3,
  overflow: 'hidden',
  boxShadow: 6,
  bgcolor: 'background.paper',
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  height: '100%',
  maxHeight: '100%',
};

const stopDrag = (event) => {
  event.stopPropagation();
};

const flipButtonSx = {
  flexShrink: 0,
  py: 1.75,
  minHeight: 56,
  borderRadius: 0,
  fontSize: '1.0625rem',
  fontWeight: 700,
  textTransform: 'none',
  lineHeight: 1.25,
  letterSpacing: 'normal',
  wordSpacing: 'normal',
  // The app font lists color-emoji faces before the generic family. If Georgia
  // is missing, those faces draw the space glyph at emoji width.
  fontFamily: (theme) => {
    const family = theme.typography?.fontFamily || 'Georgia, serif';
    const textFaces = family
      .replace(/,?\s*"(?:Apple Color Emoji|Segoe UI Emoji|Segoe UI Symbol|Noto Color Emoji)"/g, '')
      .replace(/,\s*serif\s*$/i, '');
    return `${textFaces}, serif`;
  },
};

const faceSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  width: '100%',
};

const SwipeCard = ({ book, activeQueue, isBacklogReview, onCommit, stackIndex = 0, dragDisabled = false }) => {
  const [flipped, setFlipped] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-10, 10]);

  const coverSrc = book.coverImage
    ? setOpenLibraryCoverSize(book.coverImage, OL_COVER_SIZE.L)
    : undefined;

  const [overlayDir, setOverlayDir] = useState('');
  const [overlayStrength, setOverlayStrength] = useState(0);

  const handleDrag = useCallback((_, info) => {
    const lx = info.offset.x;
    const ly = info.offset.y;
    const ax = Math.abs(lx);
    const ay = Math.abs(ly);
    if (ax < DEAD_ZONE && ay < DEAD_ZONE) {
      setOverlayDir('');
      setOverlayStrength(0);
      return;
    }
    let dir = '';
    let mag = 0;
    if (ax > ay) {
      dir = lx > 0 ? 'right' : 'left';
      mag = Math.min(1, ax / 160);
    } else {
      dir = ly < 0 ? 'up' : 'down';
      mag = Math.min(1, ay / 160);
    }
    if (isBacklogReview && (dir === 'up' || dir === 'down')) {
      setOverlayDir('');
      setOverlayStrength(0);
      return;
    }
    if (dir === 'up' && book.pool === 'backlog' && !isBacklogReview) {
      setOverlayDir('');
      setOverlayStrength(0);
      return;
    }
    setOverlayDir(dir);
    setOverlayStrength(mag);
  }, [book.pool, isBacklogReview]);

  const resolveAction = (offX, offY) => {
    const ax = Math.abs(offX);
    const ay = Math.abs(offY);
    if (ax < SWIPE_COMMIT && ay < SWIPE_COMMIT) {
      return null;
    }
    if (isBacklogReview) {
      if (ax > ay) {
        return offX > 0 ? 'like' : 'pass';
      }
      return null;
    }
    if (ax > ay) {
      return offX > 0 ? 'like' : 'pass';
    }
    if (offY < 0) {
      return book.pool === 'backlog' ? null : 'super_like';
    }
    return 'bookmark';
  };

  const handleDragEnd = async (_, info) => {
    setOverlayDir('');
    setOverlayStrength(0);
    const offX = info.offset.x;
    const offY = info.offset.y;

    if (Math.abs(offX) < DEAD_ZONE && Math.abs(offY) < DEAD_ZONE) {
      await animate(x, 0, { type: 'spring', stiffness: 400, damping: 30 });
      await animate(y, 0, { type: 'spring', stiffness: 400, damping: 30 });
      return;
    }

    const action = resolveAction(offX, offY);
    if (!action) {
      await animate(x, 0, { type: 'spring', stiffness: 400, damping: 30 });
      await animate(y, 0, { type: 'spring', stiffness: 400, damping: 30 });
      return;
    }

    const exitX = offX > 0 ? 520 : offX < 0 ? -520 : 0;
    const exitY = offY < 0 ? -520 : offY > 0 ? 520 : 0;
    if (Math.abs(offX) >= Math.abs(offY)) {
      await animate(x, exitX, { duration: 0.22 });
    } else {
      await animate(y, exitY, { duration: 0.22 });
    }
    await onCommit(book.id, action);
    x.set(0);
    y.set(0);
  };

  const scale = 1 - stackIndex * 0.035;

  const queuePhase = book._swipePhase ?? activeQueue;
  const queueMeta = getSwipeQueueMeta(queuePhase);

  const frontFace = (
    <Box sx={faceSx}>
      <Box
        sx={{
          flex: '1 1 0px',
          minHeight: 0,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          px: 1.5,
          pt: 0.5,
        }}
      >
        {coverSrc ? (
          <Box
            component="img"
            src={coverSrc}
            alt=""
            sx={bookCoverImgSx({
              width: '100%',
              height: '100%',
              maxHeight: '100%',
              borderRadius: 1,
            })}
          />
        ) : (
          <Avatar
            sx={{
              width: '100%',
              maxWidth: 280,
              height: '100%',
              maxHeight: '100%',
              minHeight: 0,
              borderRadius: 1,
            }}
            variant="rounded"
          >
            {book.title?.[0]}
          </Avatar>
        )}
      </Box>
      <Box sx={{ flexShrink: 0, px: 2, pb: 1.5, pt: 1, width: '100%' }}>
        <Typography variant="h6" align="center" fontWeight={700}>
          {book.title}
        </Typography>
        {(book.isSuperLiked || book.isLiked) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.75, mb: 0.25 }}>
            <Chip
              size="small"
              label={book.isSuperLiked ? 'You super-liked' : 'You liked'}
              color={book.isSuperLiked ? 'error' : 'success'}
              variant="outlined"
            />
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" align="center">
          {book.author || 'Unknown author'}
        </Typography>
      </Box>
      <Button
        type="button"
        variant="contained"
        size="large"
        fullWidth
        disableElevation
        onPointerDown={stopDrag}
        onClick={() => setFlipped(true)}
        sx={flipButtonSx}
      >
        Flip to read details
      </Button>
    </Box>
  );

  const backFace = (
    <Box sx={{ ...faceSx, alignItems: 'stretch', overflow: 'hidden' }}>
      <Box
        data-testid="swipe-card-details-scroll"
        sx={{
          flex: '1 1 0px',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          touchAction: 'pan-y',
          WebkitOverflowScrolling: 'touch',
          textAlign: 'left',
        }}
        onPointerDown={stopDrag}
      >
        <Box sx={{ px: 2, pt: 0.5, pb: 2 }}>
          <Box sx={{ mb: 2, minWidth: 0 }}>
            <Typography variant="h5" component="div" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
              {book.title || 'Untitled'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {book.author || 'Unknown Author'}
            </Typography>
          </Box>
          <BookInfoContent
            book={book}
            discussionDate={book.discussionDate ?? book.discussion_date}
            showProgress={false}
            showLikeToggle={false}
            clampText={false}
            sx={{ maxWidth: '100%', mx: 0 }}
          />
        </Box>
      </Box>
      <Button
        type="button"
        variant="outlined"
        size="large"
        fullWidth
        disableElevation
        onPointerDown={stopDrag}
        onClick={() => setFlipped(false)}
        sx={flipButtonSx}
      >
        Flip back to cover
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 10 - stackIndex,
        display: 'flex',
        flexDirection: 'column',
        transform: stackIndex ? `translateY(${stackIndex * 6}px) scale(${scale})` : undefined,
        transformOrigin: 'center center',
        pointerEvents: dragDisabled ? 'none' : 'auto',
        opacity: dragDisabled ? 0.85 : 1,
      }}
    >
      <motion.div
        drag={!dragDisabled && !flipped}
        dragConstraints={{ left: -400, right: 400, top: -400, bottom: 400 }}
        dragElastic={0.12}
        style={{
          x,
          y,
          rotate,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          height: '100%',
          touchAction: flipped ? 'pan-y' : 'none',
        }}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
      >
        <Box sx={cardShellSx}>
          <Box
            role="group"
            aria-label={`Book details. Queue: ${queueMeta.label}.`}
            sx={{
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              px: 1,
              pt: 1,
              pb: 0.5,
              zIndex: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                flex: 1,
                minWidth: 0,
                fontWeight: 600,
                lineHeight: 1.35,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              <Box component="span" sx={{ mr: 0.5 }} aria-hidden>
                {queueMeta.emoji}
              </Box>
              {queueMeta.label}
            </Typography>
          </Box>
          <SwipeOverlay
            direction={overlayDir}
            strength={overlayStrength}
            isBacklogReview={isBacklogReview}
          />
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={flipped ? 'back' : 'front'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 0px',
                minHeight: 0,
                width: '100%',
                position: 'relative',
                zIndex: 1,
                overflow: 'hidden',
              }}
            >
              {flipped ? backFace : frontFace}
            </motion.div>
          </AnimatePresence>
        </Box>
      </motion.div>
    </Box>
  );
};

export default SwipeCard;
